import moment from "moment-timezone";
import template from "./date.html";
import "./date.less";
import {
	debounce,
	find
} from "lodash";
import {
	EVENT_FILTER_PRIMARYDATE,
	EVENT_CLEAR_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_SET_VALUE,
	EVENT_SET_DEFAULT_VALUE,
	EVENT_REVERT_VALUES,
	EVENT_VALUE_CHANGED,
	EVENT_PAGE_LOADED,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import { DateTimeFieldAdapter } from "../../../../services/activity-trigger";

// date
export class Controller {
	selection = {};

	constructor($scope, eventBus, $timeout, $logger) {
		this._scope = $scope;
		this.$timeout = $timeout;
		this.$logger = $logger;

		this.opened = false;
		this.format = "MM/dd/yy";
		this.dateOptions = {
			formatYear: "yy",
			startingDay: 0,
			minDate: new Date(12 * 60 * 60 * 1000)
		};
		this.modelOptions = {};

		this.altInputFormats = [
			"M!-d!-yyyy", "M!-d!-yy",
			"M!/d!/yyyy", "M!/d!/yy",
			"M!.d!.yyyy", "M!.d!.yy",
			"shortDate"
		];

		this.maskOptions = {
			maskDefinitions: {
				"9": /\d/,
				"A": /[a-zA-Z]/,
				"*": /[a-zA-Z0-9]/,
				"D": /[AP]/,
				// eslint-disable-next-line no-useless-escape
				"S": /[\d\/\.-:]/,
				"M": /[0-5]/
			},
			clearOnBlur: false,
			clearOnBlurPlaceholder: false
		};
		const monthNumber = moment().format("MM");
		const year = moment().format("YY");

		this.placeholder = `Ex. ${monthNumber}/01/${year}`;

	}

	$onInit() {
		this.capabilities = {
			activityTrigger: new DateTimeFieldAdapter(
				() => !!this.selection.date,
				value => {
					this.externalSet = true;
					this.selection = { date: value };

					this.$timeout(() => this._scope.$apply());
				}
			)
		};
		this._eventBus = this.reportScope.events;
		this._unregister = this.reportFormCtrl.registerFieldController(this);

		if(this.field.MinValue !== undefined) {
			this.dateOptions.minDate = moment()
				.add(this.field.MinValue, "days")
				.toDate();
		}

		if(this.field.MaxValue !== undefined) {
			this.dateOptions.maxDate = moment()
				.add(this.field.MaxValue, "days")
				.toDate();
		}

		this.setValue();

		const ctrl = this;
		this._scope.$watch(() => ctrl.selection.date, debounce((next, prev) => {
			if(next && (prev?.getTime() !== next.getTime())) {
				ctrl.handleChange();
			}
		}, 400, {
			"leading": false,
			"trailing": true
		}), true);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (e, params) => {
			if(!this.cleared && (!!this.selection?.date) && params.isInitializing !== true) {
				this.cleared = {
					selection: {
						date: this.selection.date
					}
				};
			}
			this.selection.date = null;
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!!this.cleared) {
				this.selection = this.cleared.selection;
				this.cleared = undefined;
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (e, params) => {
			if(!this.selection.date) {
				this.externalSet = true;
				this.selection.date = params.value;

				this.$timeout(() => this._scope.$apply());
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_DEFAULT_VALUE(this.field.Id), () => {
			this.externalSet = true;
			this.selection = { 
				date: moment()
					.startOf("day")
					.toDate() 
			};

			this.$timeout(() => this._scope.$apply());
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_PAGE_LOADED, () => {
			this.initialValue = find(this.values, v => v.FieldId === this.field.Id);
			if(!!this.initialValue) {
				const localShiftedMoment = moment(this.initialValue.Date, "YYYY-MM-DDThh:mm:ss");
				this.initialValue.Date = moment(localShiftedMoment.toDate()).tz(this.field.timezone, true)
					.format("YYYY-MM-DDTHH:mm:00Z");
			}
		}, this._scope, {
			get isReady() {
				return !!ctrl.field.timezone;
			}
		});

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
	}

	getCurrentValue(isApiCall = false) {
		let result = this.buildResult();

		if(!isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true) {
			if(!result) {
				result = { FieldId: this.field.Id };
				result.hasResultForRuleRun = false;
			}
			result.isDirty = (result.Date && moment(result.Date).format()) != (this.initialValue && moment(this.initialValue.Date).format());
		}

		return result ? [result] : [];
	}

	revertValue() {
		if(this.initialValue) {
			const localShiftedMoment = this.initialValue && moment(this.initialValue.Date, "YYYY-MM-DDThh:mm:ss");

			const date = localShiftedMoment.toDate(); //this value is now clean for the field

			this.selection.date = date;
		}
		else {
			this.selection.date = undefined;
		}
	}

	setPristine() {
		this.initialValue = this.buildResult();
	}

	buildResult() {
		if(!this.field.timezone) {
			return null;
		}

		const outgoingDate = moment(this.selection.date).tz(this.field.timezone, true);

		return this.selection.date
			? {
				FieldId: this.field.Id,
				Date: outgoingDate.format()
			} : undefined;
	}

	setValue() {
		this.value = find(this.values, v => v.FieldId === this.field.Id);

		if(this.value) {
			// This is incoming data path from VESL server/API
			const localShiftedMoment = moment(this.value.Date, "YYYY-MM-DDThh:mm:ss");

			const date = localShiftedMoment.toDate(); //this value is now clean for the field

			this.selection.date = date;
		}
	}

	handleChange() {
		if(this.selection.date && !this.field.isReadonly && !this.field.isDisabled) {
			if(this.field.IsPrimary) {
				const asOf = moment(this.selection.date).tz(this.field.timezone, true);
				this._eventBus.trigger(EVENT_FILTER_PRIMARYDATE, {
					effectiveAsOf: { fromInclusive: asOf.clone().startOf("day"), toExclusive: asOf.clone().add(1, "day")
						.startOf("day"), allowPartial: true },
					autoSelect: true
				});
			}
			this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), {
				field: this.field,
				value: moment(this.selection.date).tz(this.field.timezone, true)
					.toDate()
			});

			const currentResult = this.getCurrentValue();
			const isDirty = (this.field.visible !== false || this.initialValue !== undefined) && currentResult[0].isDirty;
			this.setFieldDirty(this.field.Id, isDirty);
		}
	}

	toggle($event) {
		$event.preventDefault();
		$event.stopPropagation();
		this.opened = !this.opened;
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}
}

export default angular
	.module("vesl.components.report-form.date", [])
	.component("date", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
